import { all, call, put, select, takeLatest } from 'redux-saga/effects';
import { Action } from 'typescript-fsa';
import { formatISO } from 'date-fns';

import { hideLoader, showLoader } from '@topconagriculture/loader';
import { updateUserProfile } from '@topconagriculture/profiling';
import { generateUUID } from '@topconagriculture/frontend-utils';

import api from '../../../core/api';
import { feedingActions, groupActions, premixActions } from '../../actions';
import { getFarmsListRequest } from '../../entities/farms/farms.actions';
import { getFeedingPeriodById } from '../../entities/feeding/feeding.selector';
import { Feeding, FeedingPeriod } from '../../entities/feeding/feeding.types';
import { getSelectedGroup } from '../../selected/selected.selector';
import { groupSelector, loadMixersSelector, settingsSelector } from '../../selectors';
import { LoadersNames } from '../../ui/ui.constants';
import * as actions from './loadMixers.actions';
import { composeErrorMessage } from '../../../core/helpers/utils/utils';
import { notificationError, notificationSuccess } from '../../../core/helpers/utils/notificationUtils';
import { DataTransferMethods, FeedingInScheduling, LoadMixer } from './loadMixers.types';
import { schedulePremix } from './loadMixers.actions';
import { getDataTransferMethod, getEffectiveDate } from './loadMixers.selector';

export function* getFeedingLoadsSaga() {
  yield put(showLoader(LoadersNames.fetchLoadList));

  const mixerList = yield select(loadMixersSelector.getSelectedMixersScaleNumber);
  const checkedPeriods = yield select(loadMixersSelector.getCheckedFeedingPeriods);
  const isListMode = yield select(settingsSelector.isListMode);
  const effectiveDate = yield select(getEffectiveDate);
  const dataTransferMethod = yield select(getDataTransferMethod);

  let effectiveDateParam;

  if (dataTransferMethod === DataTransferMethods.FEEDING_SYSTEM) {
    effectiveDateParam = formatISO(effectiveDate, { representation: 'date' });
  }

  try {
    let result = yield call(
      api.feeding.fetchLoadList,
      mixerList,
      'user',
      checkedPeriods,
      isListMode,
      effectiveDateParam,
    );

    if (isListMode && result.feedings) {
      result = result.feedings.reduce(
        (acc, curr): LoadMixer[] => [
          ...acc,
          ...curr.groups.map(
            (group): LoadMixer => ({
              feedingNo: curr.feedingNo,
              ingredients: [],
              loadNo: -1,
              loadPartNo: -1,
              rationId: group.rationId,
              rationDescription: group.rationDescription,
              groups: [
                {
                  groupId: group.groupId,
                  targetWeight: group.targetWeight,
                  headCount: group.headCount,
                  groupDescription: group.groupDescription,
                },
              ],
              totalHeadCount: group.headCount,
              totalTargetWeight: group.targetWeight,
              isPremix: curr.isPremix,
              density: curr.density,
              daysOnRation: curr.daysOnRation,
              uuid: generateUUID(),
            }),
          ),
        ],
        [],
      );
    } else if (!isListMode) {
      result = result.map((x) => ({ ...x, uuid: generateUUID() }));
    }

    yield put(
      actions.getFeedingLoads.done({
        params: {},
        result,
      }),
    );
  } catch (err) {
    yield put(notificationError(composeErrorMessage(err)));
  }
  yield put(hideLoader(LoadersNames.fetchLoadList));
}

export function* updateFeedingOrderSaga(
  action: ReturnType<
    | typeof feedingActions.updateFeedingOrder
    | typeof feedingActions.updateFeedingOrderLinkBaseFeeding
    | typeof feedingActions.updateFeedingOrderLinkFeeding
  >,
) {
  const { feedingPeriodId, formName } = action.payload;
  const { id, feedings = [] }: FeedingPeriod = yield select(getFeedingPeriodById, feedingPeriodId);

  const feedingPeriod = {
    id,
    feedings: feedings.map((feeding) => ({ id: feeding.id })),
  };

  const hasSelectedFeedingPeriods = yield select(loadMixersSelector.hasSelectedFeedingPeriods);
  const hasAtLeastOneMixerSelected = yield select(loadMixersSelector.hasAtLeastOneMixerSelected);

  try {
    yield call(api.feeding.updateFeedingOrderInPeriod, feedingPeriodId, feedingPeriod, formName);
    if (hasSelectedFeedingPeriods && hasAtLeastOneMixerSelected) {
      yield call(getFeedingLoadsSaga);
    }
  } catch (err) {
    yield put(notificationError(composeErrorMessage(err)));
  }
}

export function* getLoadMixerPremixesListSaga() {
  yield put(showLoader(LoadersNames.getLoadMixersPremixes));
  try {
    const result = yield call(api.premixes.getPremixesInLoadMixer);

    yield put(
      actions.getLoadMixerPremixesList.done({
        params: {},
        result,
      }),
    );
  } catch (err) {
    yield put(notificationError(composeErrorMessage(err)));
  }
  yield put(hideLoader(LoadersNames.getLoadMixersPremixes));
}

export function* deletePremixInFeedingGroupSaga(action: Action<{ id: string; formName: any }>) {
  const { id: groupId } = yield select(getSelectedGroup);
  const { id: feedingId, formName } = action.payload;
  const hasAtLeastOneMixerSelected = yield select(loadMixersSelector.hasAtLeastOneMixerSelected);

  try {
    const result = yield call(api.group.deleteFeeding, groupId, feedingId, formName);

    yield put(feedingActions.getFeedingPeriodList.started({}));
    yield put(actions.getAllFeedingPeriodDetail.started({}));
    yield put(actions.getLoadMixerPremixesList.started({}));
    yield put(
      actions.deletePremixInFeeding.done({
        params: {},
        result,
      }),
    );
    if (hasAtLeastOneMixerSelected) {
      yield put(actions.getFeedingLoads.started({}));
    }
    yield put(groupActions.getGroupById.started({ groupId }));
  } catch (err) {
    yield put(notificationError(composeErrorMessage(err)));
  }
}

export function* sendFeedingLoadsToMixerSaga(action: ReturnType<typeof actions.sendFeedingLoadsToMixer.started>) {
  yield put(showLoader(LoadersNames.sendFeedingLoadsToMixer));

  const mixerList = yield select(loadMixersSelector.getSelectedMixersScaleNumber);
  const checkedPeriods = yield select(loadMixersSelector.getCheckedFeedingPeriods);

  const { channel, effectiveDate, formName } = action.payload;

  try {
    const result = yield call(
      api.feeding.sendFeedingLoadsToMixer,
      checkedPeriods,
      mixerList,
      channel,
      formName,
      effectiveDate ? formatISO(effectiveDate, { representation: 'date' }) : undefined,
    );
    yield put(
      actions.deletePremixInFeeding.done({
        params: {},
        result,
      }),
    );
  } catch (err) {
    yield put(notificationError(composeErrorMessage(err)));
  }
  yield put(hideLoader(LoadersNames.sendFeedingLoadsToMixer));
}

export function* schedulePremixSaga(action: ReturnType<typeof schedulePremix.started>) {
  yield put(showLoader(LoadersNames.schedulePremix));
  try {
    const { premix, formName } = action.payload;
    const feedingsSchedule: FeedingInScheduling[] = yield select(loadMixersSelector.getFeedingsScheduling);
    const isPercMethod = yield select(settingsSelector.isPremixAmountsByPercentage);
    const feedings: Feeding[] = yield select(groupSelector.getPremixFeedings, premix.premixGroupId, premix.id);
    const feedingsScheduleIds = feedingsSchedule.map((x) => x.feedingPeriodId);
    const feedingsIds = feedings.map((x) => x.feedingPeriodId);

    const feedingsAdd = feedingsSchedule.filter((x) => !feedingsIds.includes(x.feedingPeriodId));
    const feedingsUpdate = feedingsSchedule.filter((x) => feedingsIds.includes(x.feedingPeriodId));
    const feedingsRemove = feedings.filter((x) => !feedingsScheduleIds.includes(x.feedingPeriodId));

    const feedingsUpdateMapped = feedingsUpdate.map((x) => {
      const feeding = feedings.find((f) => f.feedingPeriodId === x.feedingPeriodId);
      return {
        ...x,
        feedingId: feeding ? feeding.id : '-1',
      };
    });

    yield all(feedingsRemove.map((x) => call(api.group.deleteFeeding, premix.premixGroupId, x.id, formName)));
    yield all([
      ...feedingsAdd.map((x) =>
        call(
          api.group.createFeeding,
          premix.premixGroupId,
          {
            feedingPeriodId: x.feedingPeriodId,
            rationId: premix.id,
            lockedWeight: !isPercMethod ? x.actualWeight : undefined,
            lockedPerc: isPercMethod ? x.perc : undefined,
            locked: true,
            feedingFactor: 100,
          },
          formName,
        ),
      ),
      ...feedingsUpdateMapped.map((x) =>
        call(
          api.group.updateFeeding,
          premix.premixGroupId,
          {
            id: x.feedingId,
            feedingPeriodId: x.feedingPeriodId,
            lockedPerc: isPercMethod ? x.perc : undefined,
            lockedWeight: !isPercMethod ? x.actualWeight : undefined,
            feedingFactor: 100,
            locked: true,
          },
          formName,
        ),
      ),
    ]);

    yield put(
      actions.schedulePremix.done({
        params: action.payload,
      }),
    );

    yield put(notificationSuccess('mixers:premix_schedule_success'));

    if (action.meta && typeof action.meta.onSuccess === 'function') {
      action.meta.onSuccess();
    }
  } catch (err) {
    yield put(notificationError(composeErrorMessage(err)));
  }
  yield put(hideLoader(LoadersNames.schedulePremix));
}

export function* watchLoadMixersSaga() {
  yield all([
    takeLatest(actions.getFeedingLoads.started, getFeedingLoadsSaga),
    takeLatest(updateUserProfile.done, getFeedingLoadsSaga),
    takeLatest(feedingActions.updateFeedingOrder, updateFeedingOrderSaga),
    takeLatest(feedingActions.updateFeedingOrderLinkFeeding, updateFeedingOrderSaga),
    takeLatest(feedingActions.updateFeedingOrderLinkBaseFeeding, updateFeedingOrderSaga),
    takeLatest(actions.getLoadMixerPremixesList.started, getLoadMixerPremixesListSaga),
    takeLatest(actions.getAllFeedingPeriodDetail.started, getFeedingLoadsSaga),
    takeLatest(actions.deletePremixInFeeding.started, deletePremixInFeedingGroupSaga),
    takeLatest(actions.deleteRecipeInFeeding.started, deletePremixInFeedingGroupSaga),
    takeLatest(getFarmsListRequest.done, getLoadMixerPremixesListSaga),
    takeLatest(actions.sendFeedingLoadsToMixer.started, sendFeedingLoadsToMixerSaga),
    takeLatest(actions.schedulePremix.started, schedulePremixSaga),
    takeLatest(premixActions.createPremix.done, getLoadMixerPremixesListSaga),
    takeLatest(premixActions.deletePremix.done, getLoadMixerPremixesListSaga),
  ]);
}
